import { field2ToTypedDisplayField } from '@foresightdata/web-common-core';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { FieldEntity } from '../../../data-models/field2.data-model';
import { createKpiDisplayPreferences } from '../../../data-models/preferences-v3.data-model';
import { quantitativeKpiConfigAtom } from '../../../services/state/KPIConfigState';
import { StandardFormatterId } from '../../../util/formatter-service';
import { getCurrencyAwareFormatterModel } from '../../CompanyProfiles/components/Financials/utils/getCurrencyAwareFormatterModel';
import {
  activeKpiDisplayPrefAtom,
  financialsKpiDisplayPrefAtom,
  selectedKpiConfigCompanyIdAtom,
} from './DataDisplayState';
import { SelectableField } from './FieldSelectionConfig';
import { ConfigStatus } from './useDisplayConfigState';

export function useKpiDisplayConfig() {
  const quantitativeKpis = useAtomValue(quantitativeKpiConfigAtom);
  const [savedPref, setSavedPref] = useAtom(activeKpiDisplayPrefAtom);
  const defaultPref = useAtomValue(financialsKpiDisplayPrefAtom);
  const [selectedCompanyId, setSelectedCompanyId] = useAtom(selectedKpiConfigCompanyIdAtom);
  const [localPref, setLocalPref] = useState(savedPref);
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');

  const [prevCompanyId, setPrevCompanyId] = useState(selectedCompanyId);
  if (prevCompanyId !== selectedCompanyId) {
    setPrevCompanyId(selectedCompanyId);
    setLocalPref(savedPref);
    setEditStatus('initial');
  }

  const allFields: SelectableField[] = useMemo(() => {
    return (quantitativeKpis ?? [])
      .map((kpi) => {
        const displayField = field2ToTypedDisplayField(kpi);
        const defaultFormatterId = getCurrencyAwareFormatterModel(kpi).id;
        const formatter = localPref.fieldKeyToFormatterId[displayField.key] || defaultFormatterId;
        return {
          key: displayField.key,
          label: displayField.label,
          isUniversal: kpi.entity === FieldEntity.universalKPI,
          formatConfig: {
            renderer: displayField.renderer,
            formatter,
          },
        };
      })
      .sort((a, b) => (a.label ?? a.key).localeCompare(b.label ?? b.key, undefined, { sensitivity: 'base' }));
  }, [quantitativeKpis, localPref.fieldKeyToFormatterId]);

  const selectedFields = useMemo(() => {
    const excludedSet = new Set(localPref.excludedFields);
    return allFields.filter((f) => !excludedSet.has(f.key)).map((f) => f.key);
  }, [localPref.excludedFields, allFields]);

  const onAddField = useCallback((addedField: string) => {
    setLocalPref((prev) => ({
      ...prev,
      excludedFields: prev.excludedFields.filter((key) => key !== addedField),
    }));
    setEditStatus('modified');
  }, []);

  const onRemoveField = useCallback((removedKey: string) => {
    setLocalPref((prev) => ({
      ...prev,
      excludedFields: [...prev.excludedFields, removedKey],
    }));
    setEditStatus('modified');
  }, []);

  const onFormatChange = useCallback(
    (fieldKey: string, formatterId: StandardFormatterId | string | undefined) => {
      setLocalPref((prev) => {
        const fieldKeyToFormatterId = { ...prev.fieldKeyToFormatterId };
        if (formatterId) {
          fieldKeyToFormatterId[fieldKey] = formatterId;
        } else {
          delete fieldKeyToFormatterId[fieldKey];
        }
        return { ...prev, fieldKeyToFormatterId };
      });
      setEditStatus('modified');
    },
    []
  );

  const onSave = useCallback(async () => {
    const liveKeys = new Set(allFields.map((f) => f.key));
    const pruned = createKpiDisplayPreferences({
      excludedFields: localPref.excludedFields.filter((key) => liveKeys.has(key)),
      fieldKeyToFormatterId: Object.fromEntries(
        Object.entries(localPref.fieldKeyToFormatterId).filter(([key]) => liveKeys.has(key))
      ),
    });
    setSavedPref(pruned);
    setLocalPref(pruned);
    setEditStatus('saved');
  }, [allFields, localPref, setSavedPref]);

  const onReset = useCallback(async () => {
    if (selectedCompanyId !== null) {
      setSavedPref(null);
      setLocalPref(defaultPref);
    } else {
      const resetPref = createKpiDisplayPreferences();
      setSavedPref(resetPref);
      setLocalPref(resetPref);
    }
    setEditStatus('saved');
  }, [setSavedPref, selectedCompanyId, defaultPref]);

  const onDiscard = useCallback(() => {
    setLocalPref(savedPref);
    setEditStatus('initial');
  }, [savedPref]);

  const [pendingSwitch, setPendingSwitch] = useState<number | null | undefined>(undefined);

  const onCompanyChange = useCallback(
    (newId: number | null) => {
      if (editStatus === 'modified') {
        setPendingSwitch(newId);
      } else {
        setSelectedCompanyId(newId);
      }
    },
    [editStatus, setSelectedCompanyId]
  );

  const onSaveAndSwitch = useCallback(async () => {
    await onSave();
    if (pendingSwitch !== undefined) {
      setSelectedCompanyId(pendingSwitch);
      setPendingSwitch(undefined);
    }
  }, [onSave, pendingSwitch, setSelectedCompanyId]);

  const onDiscardAndSwitch = useCallback(() => {
    onDiscard();
    if (pendingSwitch !== undefined) {
      setSelectedCompanyId(pendingSwitch);
      setPendingSwitch(undefined);
    }
  }, [onDiscard, pendingSwitch, setSelectedCompanyId]);

  const onCancelSwitch = useCallback(() => {
    setPendingSwitch(undefined);
  }, []);

  return {
    quantitativeKpis,
    allFields,
    selectedFields,
    editStatus,
    selectedCompanyId,
    pendingSwitch,
    onAddField,
    onRemoveField,
    onFormatChange,
    onSave,
    onReset,
    onDiscard,
    onCompanyChange,
    onSaveAndSwitch,
    onDiscardAndSwitch,
    onCancelSwitch,
  };
}
